/**
 * 작성자 : 홍선영
 * 날짜 : 2023.03.10
 * 기능 : 검색어를 입력하여 옵션리스트를 검색하고 값을 선택해서 setState 하는 셀렉트박스 컴포넌트
 */

import React, { SetStateAction, useEffect, useState, Dispatch, useRef, useCallback, useLayoutEffect } from 'react';
import { SelectBoxSmDropStyle } from '../assets/styles/SelectBoxSmDrop';
import { v1 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface ISearchSelectBoxMulti {
  options: any; // 셀렉트박스 배열 [{ 키네임1: 코드값1 }, { 키네임2: 코드값2 }, { 키네임3: 코드값3 }]
  defaultOption: string; // 셀렉트 디폴트옵션
  state: any[]; //  state값
  setState: Dispatch<SetStateAction<any[]>>; //  useState set액션
  stateKey: string; //  state 키값
  codeKey: string;
  disabled?: boolean;
  getBorderStyle?: any; // 선택값이 없을 때 보더 스타일
  filterbar?: 'filter-1-left' | 'filter-1-leftToCenter' | 'filter-1-center' | 'filter-1-centerToRight' | 'filter-1-right' | 'filter-2-left' | 'filter-2-center' | 'filter-2-right';
  align?: 'left' | 'right';
  dropDownWidth?: 'fit-content' | 'expand-content' | 'expand-content-md' | 'expand-content-sm' | 'expand-content-xs';
  asideMain?: boolean;
  comboWidth?: 'expand-box' | 'expand-box-md' | 'expand-box-sm';
  optionHeight?: 'height-lg' | 'height-md' | 'height-base' | 'height-sm';
  clickAble?: boolean; // 클릭 시 드롭다운 가능여부 (true: 드롭다운 안됨, false: 드롭다운 됨)
  toastContent?: string; // 드롭다운 안되는 경우 보여줄 토스트 메시지
  onClick?: () => void;
}

const SearchSelectBoxMulti = ({
  options,
  defaultOption,
  state,
  setState,
  stateKey,
  codeKey,
  disabled,
  getBorderStyle,
  filterbar,
  dropDownWidth = 'fit-content',
  align = 'left',
  asideMain = false,
  comboWidth,
  optionHeight,
  clickAble,
  toastContent,
  onClick,
}: ISearchSelectBoxMulti) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLUListElement>(null);
  //   const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<{ type: string; [key: string]: string; cdName: string }[]>(options);
  const [userInput, setUserInput] = useState({ value: '' });
  const [searchResultMenu, setSearchResultMenu] = useState([]);
  const [optionIndex, setOptionIndex] = useState(0);
  const { value } = userInput;
  const [dropdownPosition, setDropdownPosition] = useState<'bottomToTop' | 'topToBottom'>('topToBottom');

  useEffect(() => {
    if (options !== undefined && options.length > 0) {
      setSelectedOption(options);
    }
  }, [options]);

  const search = useCallback(() => {
    if (value !== '') {
      //  dropdown메뉴를 순회하면서 검색문자를 포함한 옵션배열 리턴(대소문자 구분없이 검색)
      const result: any = options?.filter((word: any) => {
        return word[codeKey].toLowerCase().includes(value.toLowerCase());
      });
      setSearchResultMenu(result);
    } else {
      //  검색입력값이 없거나 모두지워졌을때 검색메뉴배열 비우기
      setSearchResultMenu([]);
    }
  }, [value, codeKey, options]);

  useEffect(() => {
    search();
  }, [search]);

  useEffect(() => {
    setOptionIndex(0);
  }, [value, isDropdownOpen]);

  useEffect(() => {
    setState([...selectedOption]);
  }, [selectedOption]);

  // 콤보박스 클릭시 옵션리스트 열기
  const onClickDropdown = () => {
    if (clickAble && toastContent) {
      toast.warn(t(toastContent));
      return;
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        if (window.innerHeight - 72 < rect.bottom && rect.top - 80 > rect.height) {
          setDropdownPosition('bottomToTop');
        } else {
          setDropdownPosition('topToBottom');
        }
      }
    };
    updatePosition();
  }, [isDropdownOpen]);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput({ ...userInput, value: e.currentTarget.value });
  };

  // 입력값 초기화 함수 추가
  const clearInput = () => {
    setUserInput({ value: '' });
  };

  const keyboardOptions = value !== '' ? searchResultMenu : options;

  // 콤보박스에서 방향키 위아래로 움직였을 때 옵션이동
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>, option: any) => {
    const key = e.key || e.keyCode;
    if (!Array.isArray(option) || option.length === 0) return;

    if ((key === 'ArrowUp' || key === 38) && optionIndex > 0) {
      //  방향키 위
      setOptionIndex((prev) => prev - 1);
    }
    if ((key === 'ArrowDown' || key === 40) && optionIndex < option.length - 1) {
      //  방향키 아래
      setOptionIndex((prev) => prev + 1);
    }
    if (key === 'Enter' || key === 13) {
      //  엔터
      const currentOption = option[optionIndex];

      if (!currentOption) return;

      setSelectedOption((prev) => {
        if (prev.find((item) => item[stateKey] === currentOption[stateKey])) {
          return currentOption[stateKey] === '' ? [] : prev.filter((item) => item[stateKey] !== currentOption[stateKey]);
        }

        return currentOption[stateKey] === '' ? options : [...prev, currentOption];
      });
      setIsDropdownOpen(!isDropdownOpen);
      setUserInput({ value: '' });
    }
  };

  //  검색입력값이 없을 때 기본메뉴배열 노출
  const renderAllDropdownMenu = () => {
    if (value === '' && Array.isArray(options)) {
      return options?.map((el: any, i: number) => (
        <li
          className={`${state.find((item) => item[stateKey] === el[stateKey]) ? 'selectedOption' : ''} ${optionIndex === i ? 'focusedOption' : ''}`.trim() || undefined}
          key={v1()}
          role='presentation'
          onClick={() => {
            if (selectedOption.find((item) => item[stateKey] === el[stateKey])) {
              setSelectedOption(el[stateKey] === '' ? [] : (prev) => prev.filter((item) => item[stateKey] !== el[stateKey]));
            } else {
              setSelectedOption(el[stateKey] === '' ? options : (prev) => [...prev, el]);
            }
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type='checkbox' checked={!!selectedOption.find((item) => item[stateKey] === el[stateKey])} readOnly style={{ margin: 0 }} />
            <span>{el[codeKey]}</span>
          </span>
        </li>
      ));
    }
    return null;
  };

  //  검색문자를 포함하는 메뉴배열 노출
  const renderSearchDropdownMenu = () => {
    return searchResultMenu.map((el, i) => (
      <li
        className={`${selectedOption.find((item) => item[stateKey] === el[stateKey]) ? 'selectedOption' : ''} ${optionIndex === i ? 'focusedOption' : ''}`.trim() || undefined}
        key={v1()}
        role='presentation'
        onClick={() => {
          if (selectedOption.find((item) => item[stateKey] === el[stateKey])) {
            setSelectedOption(el[stateKey] === '' ? [] : (prev) => prev.filter((item) => item[stateKey] !== el[stateKey]));
          } else {
            setSelectedOption(el[stateKey] === '' ? options : (prev) => [...prev, el]);
          }
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type='checkbox' checked={!!selectedOption.find((item) => item[stateKey] === el[stateKey])} readOnly style={{ margin: 0 }} />
          <span>{el[codeKey]}</span>
        </span>
      </li>
    ));
  };

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        if (window.innerHeight - 72 < rect.bottom && rect.top - 80 > rect.height) {
          setDropdownPosition('bottomToTop');
        } else {
          setDropdownPosition('topToBottom');
        }
      }
    };
    updatePosition();
  }, [isDropdownOpen]);

  const handleClick = () => {
    onClickDropdown();
    onClick && onClick();
  };

  const emptyData = options?.length === 0;

  const renderCdName = () => {
    if (emptyData) return `${t('목록 없음')}`;
    if (selectedOption.length > 0 && selectedOption[0][codeKey] !== undefined && state.find((item) => item[stateKey] !== '')) {
      if (selectedOption.find((item) => item[stateKey] === '')) return t(`${selectedOption[0][codeKey]}`);
      if (selectedOption.length === 1) return t(`${selectedOption[0][codeKey]}`);
      return t(`${selectedOption[0][codeKey]}외 ${selectedOption.length - 1}개`);
    }
    return t(defaultOption);
  };

  return (
    <SelectBoxSmDropStyle isDropdownOpen={isDropdownOpen}>
      <ul style={getBorderStyle}>
        <li className={`defaultOption ${emptyData ? 'disabled' : ''} ${isDropdownOpen ? 'opened' : ''} ${comboWidth}`} role='presentation' onClick={handleClick}>
          <span>{renderCdName()}</span>
          <div>
            <span className='material-symbols-rounded'>keyboard_arrow_down</span>
          </div>
        </li>
        {!disabled ? (
          <ul
            className={`optionBox multiSelect ${isDropdownOpen ? 'on' : 'off'} ${dropDownWidth} ${align} ${filterbar ?? ''} ${asideMain ? 'aside-main' : ''} ${filterbar && 'filterbar'} ${
              !filterbar && dropdownPosition === 'bottomToTop' && 'bottomToTop'
            } ${optionHeight ?? ''}`}
            ref={wrapperRef}
          >
            <li className='searchBox'>
              <input type='text' onKeyDown={(e) => handleKeyDown(e, keyboardOptions)} onChange={(e) => onChangeInput(e)} placeholder={t('검색...')} value={value} />
              {value && (
                <button
                  type='button'
                  className='clear-button'
                  onClick={clearInput}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    color: '#666',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                  }}
                >
                  ×
                </button>
              )}
            </li>
            {searchResultMenu.length !== 0 ? renderSearchDropdownMenu() : renderAllDropdownMenu()}
          </ul>
        ) : null}
        <div role='button' className={` ${isDropdownOpen && 'optionBox-filter'}`} onMouseDown={() => setIsDropdownOpen(false)} aria-label='back-filter-button' tabIndex={0} />
      </ul>
    </SelectBoxSmDropStyle>
  );
};

export default React.memo(SearchSelectBoxMulti);
